import AddIcon from "@mui/icons-material/Add";
import MoreIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Unstable_Grid2";
import { memo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Virtuoso } from "react-virtuoso";
import { v4 } from "uuid";
import {
  edit as editAirSale,
  selectEditSaleAuthorizer,
  selectGrandTotal,
  selectors as airSalesSelectors,
  selectTotalNumOfSegments,
  selectTotalPrice,
  selectTotalServiceFee,
  selectTotalTax,
} from "../../redux/sales";
import { loadMoreResults, selectIsSearchInProgress } from "../../redux/search";
import { formatCurrency } from "../../utils";
import AirSaleRow from "./AirSaleRow";
import CruiseSaleRow from "./CruiseSaleRow";
import HotelSaleRow from "./HotelSaleRow";
import OtherSaleRow from "./OtherSaleRow";
import SalesContextMenu from "./SalesContextMenu";

// const rangeChanged = debounce(
//   ({ startIndex, endIndex }, { dispatch, loadMoreResults }) => {},
//   1000,
// );

// rangeChanged={(range) =>
//   rangeChanged(range, { dispatch, loadMoreResults })
// }

const SALE_ROW_BY_TYPE = {
  CR: CruiseSaleRow,
  CS: CruiseSaleRow,
  HR: HotelSaleRow,
  HS: HotelSaleRow,
  OR: OtherSaleRow,
  OS: OtherSaleRow,
};

const SaleRowMemo = memo(({ canEdit, sale }) => {
  const SaleRow = SALE_ROW_BY_TYPE[sale.type] || AirSaleRow;
  return <SaleRow canEdit={canEdit} sale={sale} />;
});

const EmptyPlaceholder = ({ context }) =>
  !context.isSearchInProgress && <div className="VirtuosoRow">No results</div>;

const Footer = ({ context }) =>
  context.isSearchInProgress && <div className="VirtuosoRow">Loading...</div>;

const Sales = ({ className }) => {
  const menuRef = useRef(null);
  const dispatch = useDispatch();

  const [isReportMenuOpen, setIsReportMenuOpen] = useState(false);

  const closeReportMenu = () => setIsReportMenuOpen(false);

  const toggleReportMenu = () => setIsReportMenuOpen(!isReportMenuOpen);

  const editSaleAuthorizer = useSelector(selectEditSaleAuthorizer);

  const airSales = useSelector(airSalesSelectors.selectAll);
  const grandTotal = useSelector(selectGrandTotal);
  const isSearchInProgress = useSelector(selectIsSearchInProgress);
  const totalNumOfSegments = useSelector(selectTotalNumOfSegments);
  const totalPrice = useSelector(selectTotalPrice);
  const totalServiceFee = useSelector(selectTotalServiceFee);
  const totalTax = useSelector(selectTotalTax);

  const onAddNewClick = () => dispatch(editAirSale({ id: v4() }));

  return (
    <Box className={className}>
      <Paper elevation={3} className="HeaderContainer">
        <Grid container xs={12} spacing={0}>
          <Grid container xs spacing={0} className="Header">
            <Grid xs={5} sm={3} md={2} lg={1.5}>
              Date
            </Grid>
            <Grid xs={4.5} sm={4} md={2.75} lg={2.25}>
              Passenger
            </Grid>
            <Grid display={{ xs: "none", md: "block", lg: "none" }} md={1}>
              Airline
            </Grid>
            <Grid display={{ xs: "none", lg: "block" }} lg={1.75}>
              Ticket/Confirmation
            </Grid>
            <Grid
              display={{ xs: "none", sm: "block" }}
              sm={3}
              md={2.5}
              lg={1.5}
            >
              Segments
              <br />
              <div className="HeaderTotalValue">{totalNumOfSegments}</div>
            </Grid>
            <Grid
              className="HeaderTotal"
              display={{ xs: "none", md: "block" }}
              md={1.25}
            >
              Price
              <br />
              <div className="HeaderTotalValue">
                {formatCurrency(totalPrice)}
              </div>
            </Grid>
            <Grid
              className="HeaderTotal"
              display={{ xs: "none", lg: "block" }}
              lg={1.25}
            >
              Service Fee
              <br />
              <div className="HeaderTotalValue">
                {formatCurrency(totalServiceFee)}
              </div>
            </Grid>
            <Grid
              className="HeaderTotal"
              display={{ xs: "none", md: "block" }}
              md={1.25}
            >
              Tax
              <br />
              <div className="HeaderTotalValue">{formatCurrency(totalTax)}</div>
            </Grid>
            <Grid className="HeaderTotal" xs={2.5} sm={2} md={1.25}>
              Total
              <br />
              <div className="HeaderTotalValue">
                {formatCurrency(grandTotal)}
              </div>
            </Grid>
          </Grid>
          <Grid className="HeaderMenuContainer">
            <IconButton ref={menuRef} onClick={toggleReportMenu}>
              <MoreIcon />
            </IconButton>
            <SalesContextMenu
              menuRef={menuRef}
              open={isReportMenuOpen}
              onClose={closeReportMenu}
            />
          </Grid>
        </Grid>
      </Paper>
      <Virtuoso
        components={{ EmptyPlaceholder, Footer }}
        context={{ isSearchInProgress }}
        data={airSales}
        endReached={() => dispatch(loadMoreResults())}
        increaseViewportBy={200}
        itemContent={(index, sale) => (
          <SaleRowMemo
            canEdit={editSaleAuthorizer(sale)}
            key={sale.id}
            sale={sale}
          />
        )}
        overscan={200}
        style={{
          height: "calc(100vh - 187px)",
          overscrollBehavior: "contain",
          scrollbarGutter: "stable",
        }}
      />
      <Fab className="AddNewButton" onClick={onAddNewClick}>
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default styled(Sales)(
  ({ theme }) => `
  .AddNewButton {
    position: fixed;
    bottom: 16px;
    right: 16px;
  }

  .HeaderContainer {
    position: relative;
    width: 100%;
    z-index: 1;
  }

  .Header {
    font-family: notosans-regular;
    font-weight: 400;
    padding: 12px 40px 12px 16px;
    background-color: ${theme.palette.header.main};
  }

  .HeaderMenuContainer {
    font-family: notosans-regular;
    font-weight: 400;
    padding: 12px 0px;
    margin-left: -48px;
    background-color: ${theme.palette.header.main};
    width: 50px;
  }

  .HeaderTotalValue {
    font-weight: 700;
  }

  .HeaderTotal {
    padding-right: 8px;
    text-align: right;
  }

  .MuiGrid2-root {
    overflow-x: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .VirtuosoRow {
    padding: 2rem;
    display: flex;
    justify-content: center;
  }
`,
);
